import type { NavigateFunction } from 'react-router-dom'
import type { AxiosError } from 'axios'

import { getSessionStorage, removeSessionStorage, setSessionStorage } from '@utils/states/sessionStorage'
import { isTokenExpired } from '@utils/functions/isTokenExpired'
import { toast } from '@utils/functions/notificationToast'
import { useUser } from '@hooks/useUser'
import { useAuthentication } from '.'

import * as apiAuth from '@services/api/authentication'

const refreshToken = async () => {
  const { setIsAuthenticated } = useAuthentication.getState()

  try {
    const refreshToken = getSessionStorage<string>('@SMARTOPS_REFRESHTOKEN')

    if (!refreshToken) throw new Error('Refresh token não encontrado')

    const res = await apiAuth.refreshToken(refreshToken!)

    setSessionStorage('@SMARTOPS_ACCESSTOKEN', res.tokens.accessToken)
    setSessionStorage('@SMARTOPS_REFRESHTOKEN', res.tokens.refreshToken)
    setSessionStorage('@SMARTOPS_USER_DATA', {
      user: res.user,
      cls: res.filiais,
      competencePeriod: res.periodosCompetencia
    })

    setIsAuthenticated(true)

    return res.tokens.accessToken
  } catch (error) {
    toast.error('Sessão expirada - redirecionando para login', (error as AxiosError)?.status)
    setTimeout(() => signOut(), 3000)
  }
}

const getExchangeAuthCode = async (code: string | null, navigate: NavigateFunction) => {
  const { setLoginRedirectUrlLoading } = useAuthentication.getState()
  const { setUserData } = useUser.getState()

  setLoginRedirectUrlLoading(true)

  try {
    const res = await apiAuth.getExchangeAuthCode(code)
    const isTokenValid = !!res.tokens.accessToken && !isTokenExpired(res.tokens.accessToken)

    const userData = {
      user: res.user,
      cls: res.filiais,
      competencePeriod: res.periodosCompetencia
    }

    setSessionStorage('@SMARTOPS_ACCESSTOKEN', res.tokens.accessToken)
    setSessionStorage('@SMARTOPS_REFRESHTOKEN', res.tokens.refreshToken)
    setSessionStorage('@SMARTOPS_USER_DATA', userData)
    setUserData(userData)

    !!isTokenValid && navigate('/')
  } catch (error) {
    toast.error(
      'Erro ao obter o código de autenticação! Você será redirecionado para a página de login.',
      (error as AxiosError)?.status
    )
    setTimeout(() => signOut(), 5000)
  } finally {
    setLoginRedirectUrlLoading(false)
  }
}

const getLoginRedirectUrl = async () => {
  const { setLoginRedirectUrlLoading } = useAuthentication.getState()

  setLoginRedirectUrlLoading(true)

  try {
    const res = await apiAuth.getLoginRedirectUrl()

    if (res?.redirectUrl) window.location.href = res.redirectUrl
  } catch (error) {
    toast.error(
      'Erro ao obter o URL de redirecionamento! Você será redirecionado para a página de login.',
      (error as AxiosError)?.status
    )
    setTimeout(() => signOut(), 5000)
  } finally {
    setLoginRedirectUrlLoading(false)
  }
}

const signOut = async () => {
  const { setIsAuthenticated, setIsSignedOutLoading } = useAuthentication.getState()

  setIsSignedOutLoading(true)

  try {
    const res = await apiAuth.getLogoutRedirectUrl()

    removeSessionStorage('@SMARTOPS_ACCESSTOKEN')
    removeSessionStorage('@SMARTOPS_REFRESHTOKEN')
    removeSessionStorage('@SMARTOPS_USER_DATA')
    removeSessionStorage('@SMARTOPS_BUDGET_POPUP_SEEN')

    setIsAuthenticated(false)

    if (res?.logoutUrl) window.location.href = res.logoutUrl
  } catch (error) {
    toast.error('Não foi possível sair da aplicação', (error as AxiosError)?.status)
  } finally {
    setIsSignedOutLoading(false)
  }
}

export { getLoginRedirectUrl, getExchangeAuthCode, refreshToken, signOut }
